import { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useAuth } from './useAuth';

export type UserRole = 'admin' | 'school_admin' | 'teacher' | 'student' | null;

interface UseRoleReturn {
  role: UserRole;
  schoolId: string | null;
  loading: boolean;
  hasRole: (requiredRole: UserRole) => boolean;
  hasAnyRole: (requiredRoles: UserRole[]) => boolean;
  isAdmin: boolean;
  isSchoolAdmin: boolean;
  isTeacher: boolean;
  isStudent: boolean;
}

/**
 * Custom hook to manage user role and permissions
 * Fetches role from Cognito custom attributes
 */
export const useRole = (): UseRoleReturn => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAttributes = async () => {
      setLoading(true);
      try {
        if (user) {
          // First, try to get attributes from the user object itself
          if (user.attributes) {
            // User object already has attributes (from currentAuthenticatedUser)
            const roleValue = user.attributes['custom:role'] as UserRole;
            const schoolIdValue = user.attributes['custom:school_id'];
            setRole(roleValue || null);
            setSchoolId(schoolIdValue || null);
          } else {
            // Fallback: Fetch user attributes from Cognito
            try {
              const attributes = await Auth.userAttributes(user);

              // Extract role from custom:role attribute
              const roleAttr = attributes.find((attr) => attr.Name === 'custom:role');
              const roleValue = roleAttr?.Value as UserRole;
              setRole(roleValue || null);

              // Extract school_id from custom:school_id attribute
              const schoolIdAttr = attributes.find((attr) => attr.Name === 'custom:school_id');
              setSchoolId(schoolIdAttr?.Value || null);
            } catch (attrError) {
              console.error('Error fetching user attributes:', attrError);
              // If we can't fetch attributes, set to null but don't throw
              setRole(null);
              setSchoolId(null);
            }
          }
        } else {
          setRole(null);
          setSchoolId(null);
        }
      } catch (error) {
        console.error('Error in fetchUserAttributes:', error);
        setRole(null);
        setSchoolId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAttributes();
  }, [user]);

  /**
   * Check if user has a specific role
   */
  const hasRole = (requiredRole: UserRole): boolean => {
    return role === requiredRole;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (requiredRoles: UserRole[]): boolean => {
    return role !== null && requiredRoles.includes(role);
  };

  return {
    role,
    schoolId,
    loading,
    hasRole,
    hasAnyRole,
    isAdmin: role === 'admin',
    isSchoolAdmin: role === 'school_admin',
    isTeacher: role === 'teacher',
    isStudent: role === 'student',
  };
};
